import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BiCartAlt } from 'react-icons/bi';
import ReactGA from 'react-ga4';
import {
    PaymentElement,
    useElements,
    useStripe
} from '@stripe/react-stripe-js';
import moment from 'moment';
import CheckoutVector from 'assets/vectors/Checkout.svg';
import Loading from 'assets/gif/loading.gif';
import axios from 'config/axios';
import Button from 'components/Button';
import TextInput from 'components/TextInput';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { initializeCart } from 'store/cart';
import { setAlert } from 'store/error';
import './index.css';

function CheckoutForm({
    total,
    paymentId,
    updateTotal
}: { 
    total: number;
    paymentId: string;
    updateTotal: React.Dispatch<React.SetStateAction<number>>;
}) {
    const [applyingCode, setApplyingCode] = useState(false);
    const [promoCode, setPromoCode] = useState('');
    const [promoCodeName, setPromoCodeName] = useState('');
    const [discountAmount, setDiscountAmount] = useState(0);
    const [paymentLoading, setPaymentLoading] = useState(false);

    const dispatch = useAppDispatch();
    const { user } = useAppSelector(state => state.user);
    const {
        bundles,
        promoCode: code
    } = useAppSelector(state => state.cart);

    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setPaymentLoading(true);

        if (total < 0.5) {
            axios.post('payments/transaction/', {
                    bundles: bundles.map(bundle => bundle.id),
                    paymentId: paymentId,
                    paymentGateway: 'STRIPE',
                    paymentAmount: total,
                    discounts: [
                        ...bundles.map(bundle => bundle.discount?.name),
                    ],
                    promoCode: code
                })
                .then(res => {
                    const { orderNumber } = res.data;

                    setPaymentLoading(false);

                    // ReactGA.event('purchase', {
                    //     currency: 'USD',
                    //     transaction_id: orderNumber,
                    //     value: total,
                    //     coupon: code,
                    //     items: [
                    //         ...bundles.filter(bundle => (
                    //             !bundle.numRequired
                    //         )).reduce((bundleItemList, bundle) => (
                    //             [
                    //                 ...bundleItemList,
                    //                 {
                    //                     item_id: bundle.id,
                    //                     item_name: bundle.name,
                    //                     price: bundle.courses.reduce((total, course) => (
                    //                             total + course.tuition
                    //                         ), 0) + bundle.tests.reduce((total, test) => (
                    //                             total + test.price
                    //                         ), 0),
                    //                     discount: bundle.discount?.type === 'Percent'
                    //                         ?   (bundle.courses.reduce((total, course) => (
                    //                                 total + course.tuition
                    //                             ), 0) + bundle.tests.reduce((total, test) => (
                    //                                 total + test.price
                    //                             ), 0)) * bundle.discount.value / 100
                    //                         :   bundle.discount?.type === 'Amount'
                    //                         ?   bundle.discount?.value
                    //                         :   0
                    //                 }
                    //             ]
                    //         ), []),
                    //         ...courses.map(course => ({
                    //             item_id: course.urlSlug,
                    //             item_name: course.name,
                    //             price: course.tuition,
                    //             discount: course.discount?.type === 'Percent'
                    //                 ?   course.tuition * course.discount.value / 100
                    //                 :   course.discount?.type === 'Amount'
                    //                 ?   course.discount.value
                    //                 :   0
                    //         })),
                    //         ...tests.map(test => ({
                    //             item_id: test.testId,
                    //             item_name: test.name,
                    //             price: test.price,
                    //             discount: test.discount?.type === 'Percent'
                    //                 ?   test.price * test.discount.value / 100
                    //                 :   test.discount?.type === 'Amount'
                    //                 ?   test.discount.value
                    //                 :   0
                    //         }))
                    //     ]
                    // });
                    
                    navigate(`/payment/${orderNumber}`, { replace: true });
                })
                .catch(error => {
                    // console.log(error);
                    dispatch(setAlert(error));

                    setPaymentLoading(false);
                    
                    // If there is an error registering student, issue a refund
                });
        }

        else {
            if (!stripe || !elements) {
                return;
            }

            const result = await stripe.confirmPayment({
                elements, 
                confirmParams: {
                    receipt_email: user.email,
                    return_url: `${process.env.CLIENT_BASE_URL}/payment/stripe${code ? `?promoCode=${code}` : ''}`
                }
            });

            setPaymentLoading(false);

            if (result.error) {
                // console.log(result.error.message);
                dispatch(setAlert({ 
                    response: { 
                        data: { 
                            message: result.error.message
                        }
                    }
                }));
            }
        }
    }

    const applyPromoCode = () => {
        setApplyingCode(true);
        
        axios.post('payments/promoCode/', {
                promoCode: promoCode,
                total: total,
                paymentId: paymentId
            })
            .then(res => {
                const {
                    name,
                    discountAmount,
                    newTotal
                } = res.data;

                // console.log(res.data);

                dispatch(initializeCart({
                    'promoCode': promoCode
                }));
                updateTotal(newTotal);
                setPromoCodeName(name);
                setDiscountAmount(discountAmount);
                setApplyingCode(false);
            })
            .catch(error => {
                dispatch(setAlert(error));
                setPromoCode('');
                setApplyingCode(false);
            });
    }

    const cardContent = <>
        <h2 className='checkout__cardHeader'>
            <BiCartAlt className='checkout__cardHeaderIcon' />
            Order Summary
        </h2>
        {bundles.filter(bundle => bundle.tests?.length > 0).map(bundle => (
            <div key={bundle.name} className='checkout__cardItem'>
                <div className='checkout__cardItemHeader'>
                    <h4 className='checkout__cardItemName'>
                        {bundle.name}
                    </h4>
                    {bundle.discount
                    ?   <p className='checkout__cardItemDiscount'>
                            <span>${bundle.price.toFixed(2)}</span>
                            ${bundle.discount.type === 'Percent'
                                ? (bundle.price*(100 - bundle.discount.value)/100).toFixed(2)
                                : bundle.discount.type === 'Amount'
                                ? (bundle.price - bundle.discount.value).toFixed(2)
                                : bundle.price.toFixed(2)
                            }
                        </p>
                    :   <p className='checkout__cardItemPrice'>
                            ${bundle.price.toFixed(2)}
                        </p>
                    }
                </div>
                {bundle.description && <p className='checkout__cardItemRow'>{bundle.description}</p>}
                {/* {bundle.tests.map(test => (
                    <p className='checkout__cardItemRow'>
                        {test}
                    </p>
                ))} */}
            </div>
        ))}
        
        {promoCodeName
        ?   <div className='checkout__cardItemHeader'>
                <h4 className='checkout__promoCodeName'>
                    {promoCodeName}
                </h4>
                <p className='checkout__promoCodeDiscount'>
                    -${discountAmount.toFixed(2)}
                </p>
            </div>
        :   <div className='checkout__promo'>
                <TextInput 
                    className='checkout__promoCode'
                    name='promoCode'
                    label='promo code'
                    onChange={(event) => setPromoCode(event.target.value)}
                    value={promoCode}
                />
                <Button
                    className='checkout__promoButton'
                    onClick={applyPromoCode}
                    disabled={applyingCode}
                    type='button'
                >
                    apply
                </Button>
            </div>
        }
        
        <div className='checkout__cardTotal'>
            <p>Your Total</p>
            <b>${total.toFixed(2)}</b>
        </div>
    </>;

    return (
        <div className='layout'>
            <div className='checkout'>
                <div className='checkout__card'>
                    {cardContent}
                </div>
                <form className='checkout__form' onSubmit={handleSubmit}>
                    {/* <img
                        className='checkout__vector'
                        src={CheckoutVector}
                        alt='Checkout'
                    /> */}
                    <h1 className='checkout__header'>
                        Payment Information
                    </h1>
                    <div className='checkout__card--inline'>
                        {cardContent}
                    </div>
                    <PaymentElement />
                    <Button
                        className='checkout__button'
                        fullWidth
                        disabled={paymentLoading}
                        type='submit'
                    >
                        {paymentLoading
                            ? <img src={Loading} style={{ 'width': '18px' }} />
                            : 'PAY NOW'
                        }
                    </Button>
                </form>
            </div>
        </div>
    );
}

export default CheckoutForm;